import axios from 'axios';
import { getApiErrorMessage, isCanceledError } from './errorUtils';

// Use absolute URL to bypass proxy issues, or relative if REACT_APP_API_URL is not set
// The proxy in package.json should handle this, but if it doesn't work, use absolute URL
// Note: Port 5000 is often used by macOS AirPlay, so we use 5001
let API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Ensure API_BASE_URL ends with /api (backend routes are under /api)
if (API_BASE_URL && !API_BASE_URL.endsWith('/api')) {
  // If it doesn't end with /api, append it
  API_BASE_URL = API_BASE_URL.endsWith('/') ? `${API_BASE_URL}api` : `${API_BASE_URL}/api`;
}

// Avoid console logging in production

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout (increased for cold starts on Render free tier)
});

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // Start with 1 second

// Helper function to delay execution
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Helper function to check if error is retryable
const isRetryableError = (error) => {
  // Retry on network errors or 5xx server errors
  if (!error.response) {
    return true; // Network error (timeout, connection error, etc.)
  }
  const status = error.response.status;
  return status >= 500 || status === 429; // Server errors or rate limit
};

// Refresh token state
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Send client timezone offset (minutes) so backend can compute local dates
    config.headers['X-Timezone-Offset'] = new Date().getTimezoneOffset();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle responses with retry logic and refresh token support
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Silently ignore canceled requests (e.g., from AbortController)
    if (isCanceledError(error)) {
      return Promise.reject(error);
    }

    const originalRequest = error.config || {};
    if (!originalRequest.__globalToastShown && !originalRequest.__skipGlobalToast) {
      const shouldToast = !error.response;
      if (shouldToast) {
        originalRequest.__globalToastShown = true;
        const message = getApiErrorMessage(error, 'Request failed');
        if (message) {
          window.dispatchEvent(new CustomEvent('app-toast', {
            detail: { message, type: 'error' }
          }));
        }
      }
    }

    // Handle 401 errors (unauthorized) with refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      const currentPath = window.location.pathname;
      const publicPaths = ['/', '/login', '/register', '/verify-email'];
      const isPublicPath = publicPaths.includes(currentPath);

      // Don't try to refresh if this IS the refresh request or login/register
      const isRefreshEndpoint = originalRequest.url && originalRequest.url.includes('/auth/refresh');
      const isAuthEndpoint = originalRequest.url && (
        originalRequest.url.includes('/auth/login') ||
        originalRequest.url.includes('/auth/register')
      );

      if (isRefreshEndpoint || isAuthEndpoint) {
        return Promise.reject(error);
      }

      const refreshToken = localStorage.getItem('refreshToken');

      if (refreshToken) {
        if (isRefreshing) {
          // Queue this request until refresh completes
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            originalRequest._retry = true;
            return api(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const response = await axios.post(
            `${API_BASE_URL}/auth/refresh`,
            {},
            { headers: { Authorization: `Bearer ${refreshToken}` } }
          );

          const newAccessToken = response.data.access_token;
          localStorage.setItem('token', newAccessToken);

          processQueue(null, newAccessToken);

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          // Refresh failed - clear all tokens and redirect
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          if (!isPublicPath) {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        // No refresh token available
        const isAuthMeEndpoint = originalRequest.url && originalRequest.url.includes('/auth/me');

        if (!isAuthMeEndpoint) {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          if (!isPublicPath) {
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    }

    // Retry logic for retryable errors
    if (!originalRequest.__retryCount) {
      originalRequest.__retryCount = 0;
    }

    if (originalRequest.__retryCount < MAX_RETRIES && isRetryableError(error)) {
      originalRequest.__retryCount += 1;
      // Exponential backoff: wait 1s, 2s, 4s
      const delayMs = RETRY_DELAY * Math.pow(2, originalRequest.__retryCount - 1);
      await delay(delayMs);
      return api(originalRequest);
    }

    return Promise.reject(error);
  }
);

export default api;
export { API_BASE_URL };
